// src/services/api.js
import axios from 'axios';
import AppConst from '../apis/appconst';

// ============================================
// NETWORK-BASED LOGIC:
// If device is ONLINE → use ONLINE_URL (server)
// If device is OFFLINE → use LOCAL_URL (localhost)
// ============================================

let _currentAPI = navigator.onLine ? AppConst.ONLINE_URL : AppConst.LOCAL_URL;
let _isLocal = !navigator.onLine;

// Set initial BASE_URL based on network status
AppConst.BASE_URL = _currentAPI;
console.log(`[API] Initial network status: ${navigator.onLine ? 'ONLINE' : 'OFFLINE'} → Using: ${_currentAPI}`);

// Create axios instance
const apiClient = axios.create({
    timeout: 15000,
});

// REQUEST INTERCEPTOR: prepend correct URL to every request
apiClient.interceptors.request.use(
    (config) => {
        // Always use the latest _currentAPI (may have been switched)
        if (config.url && !config.url.startsWith('http')) {
            config.url = _currentAPI + config.url;
        }
        console.log('[API] →', config.method?.toUpperCase(), config.url);
        return config;
    },
    (error) => Promise.reject(error)
);

// RESPONSE INTERCEPTOR: fallback logic if request fails
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Don't retry if already retried
        if (originalRequest._retry) {
            return Promise.reject(error);
        }

        // Network/CORS error (no response) → try the other server
        if (!error.response && !originalRequest._retry) {
            originalRequest._retry = true;

            // If was trying online, fallback to local
            if (originalRequest.url?.includes(AppConst.ONLINE_URL)) {
                const newUrl = originalRequest.url.replace(AppConst.ONLINE_URL, AppConst.LOCAL_URL);
                console.log('[API] Online failed, retrying LOCAL:', newUrl);
                originalRequest.url = newUrl;
                _currentAPI = AppConst.LOCAL_URL;
                _isLocal = true;
                AppConst.BASE_URL = AppConst.LOCAL_URL;

                // Notify App about server switch
                window.dispatchEvent(new CustomEvent('server-switched', {
                    detail: { to: 'local', reason: 'online-failed' }
                }));

                return axios(originalRequest);
            }

            // If was trying local, fallback to online
            if (originalRequest.url?.includes(AppConst.LOCAL_URL)) {
                const newUrl = originalRequest.url.replace(AppConst.LOCAL_URL, AppConst.ONLINE_URL);
                console.log('[API] Local failed, retrying ONLINE:', newUrl);
                originalRequest.url = newUrl;
                _currentAPI = AppConst.ONLINE_URL;
                _isLocal = false;
                AppConst.BASE_URL = AppConst.ONLINE_URL;

                // Notify App about server switch
                window.dispatchEvent(new CustomEvent('server-switched', {
                    detail: { to: 'online', reason: 'local-failed' }
                }));

                return axios(originalRequest);
            }
        }

        return Promise.reject(error);
    }
);

// Listen for network changes and switch URLs automatically
const handleOnline = () => {
    console.log('[API] Network ONLINE → Switching to ONLINE_URL');
    _currentAPI = AppConst.ONLINE_URL;
    _isLocal = false;
    AppConst.BASE_URL = AppConst.ONLINE_URL;
};

const handleOffline = () => {
    console.log('[API] Network OFFLINE → Switching to LOCAL_URL');
    _currentAPI = AppConst.LOCAL_URL;
    _isLocal = true;
    AppConst.BASE_URL = AppConst.LOCAL_URL;
};

window.addEventListener('online', handleOnline);
window.addEventListener('offline', handleOffline);

// Exports
export const getCurrentAPI = async () => _currentAPI;
export const forceServerCheck = () => {}; // No-op (network events handle switching)
export const isUsingLocalServer = () => _isLocal;
export const getCurrentAPISync = () => _currentAPI;

export const switchToOnline = () => {
    _currentAPI = AppConst.ONLINE_URL;
    _isLocal = false;
    AppConst.BASE_URL = AppConst.ONLINE_URL;
    console.log('[API] Manually switched to ONLINE');
};

export const switchToLocal = () => {
    _currentAPI = AppConst.LOCAL_URL;
    _isLocal = true;
    AppConst.BASE_URL = AppConst.LOCAL_URL;
    console.log('[API] Manually switched to LOCAL');
};

export { apiClient };
export default {
    getCurrentAPI,
    forceServerCheck,
    isUsingLocalServer,
    getCurrentAPISync,
    apiClient,
    switchToOnline,
    switchToLocal
};